import { JWTRole } from "@hackthenorth/north";
import React, { useEffect } from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts";
import { useHackerContext } from "src/shared/contexts/HackerContext";
import { useFindMyBusesByEventQuery } from "src/views/hacker/rsvp/travel-form/graphql/findMyBusesByEvent.generated";

import {
  buildTravelContext,
  getTravelContextEntries,
  TravelContextPayload,
} from "./travelContext";

const VOICEFLOW_PROJECT_ID = "69f40db39f4e70343522f0e4";
const VOICEFLOW_SCRIPT_ID = "voiceflow-chat-widget-script";
const VOICEFLOW_TARGET_ID = "travel-support-chat";
const TRAVEL_GUIDELINES_URL = "https://hackthenorth.com/travel-guidelines";

type VoiceflowChat = {
  load: (config: {
    verify: { projectID: string };
    url: string;
    versionID: string;
    userID: string;
    voice: { url: string };
    render: { mode: "embedded"; target: HTMLElement | null };
    launch: {
      event: { type: "launch"; payload: TravelContextPayload };
    };
  }) => void;
  destroy: () => void;
};

declare global {
  interface Window {
    voiceflow?: { chat: VoiceflowChat };
  }
}

const TravelSupport: React.FC = () => {
  const { email, info, roles, userFound } = useUserContext();
  const { claimData, claimFound } = useHackerContext();
  const isHacker = roles.includes(JWTRole.HACKER);
  const { data: registeredBusesData, loading: registeredBusesLoading } =
    useFindMyBusesByEventQuery({
      variables: { eventSlug: CURRENT_EVENT_SLUG },
      skip: !isHacker,
    });

  const isTravelContextLoading =
    userFound === null ||
    (isHacker && (claimFound === null || registeredBusesLoading));
  const travelContext = buildTravelContext(
    isHacker && claimFound ? claimData : undefined,
    registeredBusesData?.findMyBusesByEvent ?? [],
    { displayName: info?.name, email }
  );
  const serializedTravelContext = JSON.stringify(travelContext);
  const travelContextEntries = getTravelContextEntries(travelContext);

  useEffect(() => {
    if (!email || isTravelContextLoading) return;

    const launchPayload = JSON.parse(
      serializedTravelContext
    ) as TravelContextPayload;
    let chatLoaded = false;

    const loadChat = () => {
      const target = document.getElementById(VOICEFLOW_TARGET_ID);
      if (!window.voiceflow || !target) return;

      window.voiceflow.chat.load({
        verify: { projectID: VOICEFLOW_PROJECT_ID },
        url: "https://general-runtime.voiceflow.com",
        versionID: "production",
        userID: email,
        voice: {
          url: "https://runtime-api.voiceflow.com",
        },
        render: {
          mode: "embedded",
          target,
        },
        launch: {
          event: {
            type: "launch",
            payload: launchPayload,
          },
        },
      });
      chatLoaded = true;
    };

    const existingScript = document.getElementById(
      VOICEFLOW_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (window.voiceflow) {
      loadChat();
    } else if (existingScript) {
      existingScript.addEventListener("load", loadChat);
    } else {
      const script = document.createElement("script");
      script.id = VOICEFLOW_SCRIPT_ID;
      script.src = "https://cdn.voiceflow.com/widget-next/bundle.mjs";
      script.type = "text/javascript";
      script.addEventListener("load", loadChat);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(VOICEFLOW_SCRIPT_ID);
      script?.removeEventListener("load", loadChat);
      if (chatLoaded) window.voiceflow?.chat.destroy();
    };
  }, [email, isTravelContextLoading, serializedTravelContext]);

  return (
    <TravelSupportPage>
      <Header
        title="Travel Support"
        subtitle={
          <>
            Answer your quick travel FAQ questions using our AI chatbot. Make
            sure to always double-check rules, especially around reimbursement,
            with the{" "}
            <TravelGuidelinesLink href={TRAVEL_GUIDELINES_URL}>
              travel guidelines
            </TravelGuidelinesLink>
            .
          </>
        }
      />
      <ChatPage fullWidth>
        <TravelSupportLayout>
          <ChatContainer id={VOICEFLOW_TARGET_ID} />
          <ContextPanel>
            <ContextTitle>Your travel context</ContextTitle>
            <ContextDescription>
              Based on your RSVP and travel plan, this is your travel context
              that the agent has access to.
            </ContextDescription>
            <ContextScrollArea>
              {isTravelContextLoading ? (
                <EmptyContext>Loading your travel context…</EmptyContext>
              ) : travelContextEntries.length ? (
                <ContextList>
                  {travelContextEntries.map(({ key, label, value }) => (
                    <ContextRow key={key}>
                      <ContextTerm>{label}</ContextTerm>
                      <ContextValue>{value}</ContextValue>
                    </ContextRow>
                  ))}
                </ContextList>
              ) : (
                <EmptyContext>
                  No saved RSVP or travel-plan details were found for this
                  account.
                </EmptyContext>
              )}
            </ContextScrollArea>
            <ConsentNote>
              By starting a new chat with the bot, you agree to share your
              travel context as stated above with Voiceflow and its downstream
              model providers.
            </ConsentNote>
          </ContextPanel>
        </TravelSupportLayout>
      </ChatPage>
    </TravelSupportPage>
  );
};

const TravelGuidelinesLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

const TravelSupportPage = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  min-height: 0;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.medium`
    height: auto;
    min-height: 100vh;
    overflow: visible;
  `}
`;

const ChatPage = styled(PageWrapper)`
  flex: 1 1 0;
  height: auto;
  max-height: none;
  min-height: 0;
  overflow: hidden;

  > div {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  ${({ theme }) => theme.mediaQueries.medium`
    height: auto;
    max-height: none;
    overflow: visible;

    > div {
      height: auto;
      overflow: visible;
    }
  `}
`;

const TravelSupportLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  grid-template-rows: minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.medium`
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    height: auto;
    overflow: visible;
  `}
`;

const ChatContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: ${({ theme }) => theme.globalConstants.borderRadius.medium}px;

  ${({ theme }) => theme.mediaQueries.medium`
    height: 70vh;
    min-height: 560px;
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    height: 68vh;
    min-height: 480px;
  `}
`;

const ContextPanel = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 20px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: ${({ theme }) => theme.globalConstants.borderRadius.medium}px;

  ${({ theme }) => theme.mediaQueries.medium`
    height: 70vh;
    max-height: 70vh;
    min-height: 480px;
  `}
`;

const ContextTitle = styled.h2`
  flex: 0 0 auto;
  margin: 0 0 8px;
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 20px;
  font-weight: 900;
`;

const ContextDescription = styled.p`
  flex: 0 0 auto;
  margin: 0 0 10px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  line-height: 1.5;
`;

const ContextScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const ContextList = styled.dl`
  margin: 0;
`;

const ContextRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 8px;
  padding: 10px 0;
  border-top: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderLight};
`;

const ContextTerm = styled.dt`
  min-width: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
`;

const ContextValue = styled.dd`
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 13px;
  line-height: 1.45;
  text-align: right;
`;

const EmptyContext = styled.p`
  margin: 0;
  padding: 16px 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
`;

const ConsentNote = styled.p`
  flex: 0 0 auto;
  margin: 0;
  padding-top: 12px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 11px;
  line-height: 1.45;
  border-top: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderLight};
`;

export default TravelSupport;
